import {useEffect, useMemo, useRef, useState} from "react";
import {convertPixelPitchForTicker} from "../helpers";
import { type DisplayType, FastLaneSection, type PixelMap } from '../types';
import {useDisplayType, usePixelMap} from "../store/fast-lane-context";
import {useFastLane} from "../store";
import {useInView} from "framer-motion";

export function useCalculateDims(section: FastLaneSection) {
    const left = useCalculateLeft(section);
    const width = useCalculateWidth({section});

    return {left, width};
}

export function calculateLeftOffset({section, pixelMap}: { section: FastLaneSection, pixelMap: PixelMap }) {
    const sectionLeft = section.position?.left ?? 0;
    const globalOffsetLeft = pixelMap?.offsetLeft ?? 0;

    return globalOffsetLeft + sectionLeft;
}

export function calculateLeft({section, display, pixelMap, isEditor}: { section: FastLaneSection, display: string, pixelMap: any, isEditor: boolean }) {
    const left = calculateLeftOffset({section, pixelMap});

    if (isEditor || display !== "ticker") {
        return left;
    }

    const sectionOffsetLeft = section.position?.tickerOffsetLeft ?? 0;

    return convertPixelPitchForTicker(left + sectionOffsetLeft, pixelMap);
}

export function useCalculateLeft(section: FastLaneSection) {
    const pixelMap = usePixelMap();
    const display = useDisplayType();
    const isEditor = useFastLane(f => f.isEditor);

    return useMemo(() => {
        return calculateLeft({section, display, pixelMap, isEditor});
    }, [isEditor, display, pixelMap, section]);
}

export function useSpotlightOpacity(index: number) {
    const display = useDisplayType();
    const takeover = useFastLane(f => f.takeover);
    const [opacity, setOpacity] = useState(1);

    useEffect(() => {
        if (!takeover?.enabled || display === 'ticker') {
            return;
        }
        const spotlightIndex = takeover?.spotlightEntryIndex;

        if (spotlightIndex === undefined || spotlightIndex === 'all') {
            return;
        }

        if (spotlightIndex !== index) {
            setOpacity(0.01);

            setTimeout(() => {
                setOpacity(1);
            }, takeover.takeOverDuration + 2_500);
        }

    }, [takeover, display]);

    return opacity;
}

export function calculateWidth(section: FastLaneSection, pixelMap: PixelMap, display: DisplayType, isLastInGroup = false) {
    const nextSection = useFastLane.getState().entries?.[section.index + 1];

    if (!nextSection?.position?.left) {
        return section.position?.width;
    }

    const width = isLastInGroup ? section.position?.width : nextSection?.position?.left - (section.position?.left ?? 0);

    if (display === 'ticker' && pixelMap) {
        return convertPixelPitchForTicker(width, pixelMap);
    }

    return width;
}

export function useCalculateWidth({section}: { section: FastLaneSection }) {
    const pixelMap = usePixelMap();
    const display = useDisplayType();

    return useMemo(() => {
        return calculateWidth(section, pixelMap, display);
    }, [section]);
}

export function useShowContent() {
    const containerRef = useRef(null);
    const isEditor = useFastLane(f => f.isEditor);
    const isInView = useInView(containerRef, {
        amount: "some",
    });
    const showContent = isEditor || isInView;

    return {containerRef, showContent};
}
